/** Angular */
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
/** Enums */
import { BydStorageKeysEnum } from '../enums/byd-storage-keys.enum';
/** Services */
import { BydConfigService } from './byd-config.service';
/** Constants */
import { EMPTY_STRING } from '../shared/empty-string.constant';

/** Resolve type DOCUMENT in Aot */
/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class BydTokenService {
  /**
   * The user token.
   */
  private _token =
    localStorage.getItem(BydStorageKeysEnum.TOKEN_KEY) ||
    this.getQueryParams(BydStorageKeysEnum.TOKEN_KEY) ||
    EMPTY_STRING;

  /**
   * The token getter.
   *
   * @returns token
   */
  public get token(): string {
    return this._token;
  }

  /**
   * The user refresh token.
   */
  private _refreshToken =
    localStorage.getItem(BydStorageKeysEnum.REFRESH_TOKEN_KEY) ||
    this.getQueryParams(BydStorageKeysEnum.REFRESH_TOKEN_KEY) ||
    EMPTY_STRING;

  /**
   * The refreshToken getter.
   *
   * @returns refreshToken
   */
  public get refreshToken(): string {
    return this._refreshToken;
  }

  /**
   * Creates an instance of BydRhsTokenService.
   *
   * @param bydConfigService the api configuration service
   * @param route the route information associated with the component
   * @param document the DOM tree
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(protected bydConfigService: BydConfigService<any>, @Inject(DOCUMENT) private document: Document) {
    if (this.token) this.updateToken();
  }

  /**
   * Update user token in local storage, headers & token property.
   *
   * @param token the user token
   * @param refreshToken the user refresh token
   */
  public updateToken(token = this.token, refreshToken = this.refreshToken): void {
    localStorage.setItem(BydStorageKeysEnum.TOKEN_KEY, token);
    localStorage.setItem(BydStorageKeysEnum.REFRESH_TOKEN_KEY, refreshToken);
    this.bydConfigService.headers = this.setHeadersAuthorization(token);
    this._token = token;
    this._refreshToken = refreshToken;
  }

  /**
   * Remove token from local storage & `BydConfigService` headers.
   */
  public removeToken(): void {
    this.bydConfigService.headers = this.bydConfigService.headers.delete(BydStorageKeysEnum.AUTHORIZATION_KEY);
    localStorage.removeItem(BydStorageKeysEnum.TOKEN_KEY);
    localStorage.removeItem(BydStorageKeysEnum.REFRESH_TOKEN_KEY);
    this._token = EMPTY_STRING;
    this._refreshToken = EMPTY_STRING;
  }

  /**
   * Sets Authorization header in a clone of the original instance.
   * If the header already exists,
   * its value is replaced with the given value in the returned object.
   *
   * @param token the connected user token
   * @returns headers
   */
  private setHeadersAuthorization(token: string) {
    return this.bydConfigService.headers.set(BydStorageKeysEnum.AUTHORIZATION_KEY, `Bearer ${token}`);
  }

  /**
   * Get the first param associated to the given search parameter.
   *
   * @param param the searched query param
   * @returns string
   */
  private getQueryParams(param: string) {
    return new URLSearchParams(this.document?.location?.search).get(param);
  }
}
